import { Add, Delete, Edit } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FlexGrid,
  FlexGridCellTemplate,
  FlexGridColumn,
} from "@grapecity/wijmo.react.grid";
import { FlexGridFilter } from "@grapecity/wijmo.react.grid.filter";
import { FlexGridXlsxConverter } from "@grapecity/wijmo.grid.xlsx";
import { useEffect, useMemo, useRef, useState } from "react";
import excel from "src/assets/images/excelIcon.png";
import SimpleColumnPicker from "src/qubCommon/Grids/SimpleColumnPicker";
import ReviewCommentsDialog, {
  ReviewCommentsCell,
} from "./ReviewCommentsDialog";

const rowActionButtonSx = {
  height: 26,
  width: 26,
};

const REVIEW_COLUMN_CLASS = "expense-review-column";
const REVIEW_COLUMN_HEADER_CLASS = "expense-review-column-header";
const REVIEW_COLUMN_BINDINGS = new Set(["expStatusAbbr", "statusComments"]);
const PAYMENT_GRID_LABELS = {
  paymentType: "Είδος Πληρωμής",
  paymentDate: "Ημ/νία Πληρωμής",
  paymentNumber: "Αριθμός",
  cashAmount: "Μετρητά",
  cardAmount: "Κάρτα",
  bankAmount: "Τραπ. Κατάθεση",
  amount: "Ποσό",
  reviewStatus: "Κατάσταση Ελέγχου",
  reviewComments: "Σχόλια Ελέγχου",
  pendingReview: "Προς έλεγχο",
  comments: "Σχόλια",
  remarks: "Παρατηρήσεις",
  addPayment: "Προσθήκη Πληρωμής",
  exportExcel: "Εξαγωγή σε Excel",
  totalPayments: "Σύνολο πληρωμών",
  editPayment: "Επεξεργασία πληρωμής",
  deletePayment: "Διαγραφή πληρωμής",
  noPayments: "Δεν έχουν καταχωρηθεί πληρωμές.",
};

const PAYMENT_REVIEW_STATUS_OPTIONS = [
  { value: "125", label: PAYMENT_GRID_LABELS.pendingReview },
  {
    value: "150",
    label: "Εγκεκριμένη",
  },
  {
    value: "190",
    label: "Απορριφθείσα",
  },
];

const reviewInputSx = {
  "& .MuiInputBase-root": {
    fontSize: 13,
    height: 24,
    minHeight: 24,
  },
  "& .MuiInputBase-input": {
    px: 0.75,
    py: 0.35,
  },
};

const reviewGridSx = {
  "& .wj-flexgrid .wj-cell.expense-review-column": {
    backgroundColor: "#fff7ed",
    borderColor: "#fed7aa",
  },
  "& .wj-flexgrid .wj-cell.expense-review-column-header": {
    backgroundColor: "#ffedd5",
    borderColor: "#fb923c",
    color: "#9a3412",
    fontWeight: 800,
  },
  "& .wj-flexgrid .wj-cell.expense-review-column.wj-state-selected": {
    backgroundColor: "#fdba74",
    color: "#431407",
  },
  "& .wj-flexgrid .wj-cell.expense-review-column .MuiInputBase-root": {
    backgroundColor: "transparent",
  },
};

const stopGridEvent = (event) => {
  event.stopPropagation();
};

const normalizeStatusValue = (value) =>
  value === undefined || value === null || value === "" ? "125" : value.toString();

const getPaymentReviewStatusId = (payment) =>
  payment?.expStatusId ?? payment?.statusId ?? payment?.ExpStatusId ?? payment?.StatusId;

const getPaymentReviewStatusLabel = (payment, value) =>
  payment?.expStatusAbbr ??
  payment?.statusAbbr ??
  payment?.ExpStatusAbbr ??
  payment?.StatusAbbr ??
  statusLabelByValue[value] ??
  PAYMENT_GRID_LABELS.pendingReview;

const statusLabelByValue = PAYMENT_REVIEW_STATUS_OPTIONS.reduce(
  (acc, option) => ({
    ...acc,
    [option.value]: option.label,
  }),
  {},
);

const currencyFormatter = new Intl.NumberFormat("el-GR", {
  currency: "EUR",
  style: "currency",
});

const formatCurrency = (value) => currencyFormatter.format(Number(value ?? 0));

const formatValue = (value) => {
  if (value === undefined || value === null || value === "") return "-";
  return value;
};

const ReadOnlyCell = ({ value, align = "left", muted = false }) => (
  <Box
    sx={{
      alignItems: "center",
      color: muted ? "#667085" : "#111827",
      display: "flex",
      fontSize: 13,
      fontWeight: 400,
      height: "100%",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      overflow: "hidden",
      textAlign: align,
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
    title={value ?? ""}
  >
    {formatValue(value)}
  </Box>
);

const sumPaymentAmounts = (payments = []) =>
  payments.reduce((sum, payment) => sum + Number(payment?.amount ?? 0), 0);

const ExpensePaymentsGrid = ({
  transaction,
  payments = transaction?.payments ?? [],
  addPaymentSetting = transaction?.settings?.addPayment,
  isAddPaymentDisabled = false,
  showReviewStatus = false,
  canReview = false,
  onAddPayment,
  onPaymentAction,
  onReviewPaymentChange,
}) => {
  const gridRef = useRef(null);
  const [columnPickerRefreshKey, setColumnPickerRefreshKey] = useState(0);
  const forceHiddenCols = useMemo(
    () => (showReviewStatus ? [] : ["expStatusAbbr", "statusComments"]),
    [showReviewStatus],
  );
  const [commentSelection, setCommentSelection] = useState(null);
  const hasRowActions = useMemo(
    () =>
      payments.some(
        (payment) => payment?.canEdit === true || payment?.canDelete === true,
      ),
    [payments],
  );
  const canAddPayment =
    addPaymentSetting?.can === true || transaction?.canAddPayment === true;
  const addPaymentText = addPaymentSetting?.txt ?? PAYMENT_GRID_LABELS.addPayment;
  const paidAmount = transaction?.paidAmount ?? sumPaymentAmounts(payments);
  const paymentsForGrid = useMemo(
    () =>
      payments.map((payment) => ({
        ...payment,
        cashAmount: payment?.cashAmount ?? 0,
        cardAmount: payment?.cardAmount ?? 0,
        bankAmount: payment?.bankAmount ?? 0,
        amount: payment?.amount ?? 0,
      })),
    [payments],
  );

  useEffect(() => {
    if (!gridRef.current) return;

    setColumnPickerRefreshKey((currentKey) => currentKey + 1);
  }, [hasRowActions]);

  const handleGridInitialized = (grid) => {
    gridRef.current = grid;
    setColumnPickerRefreshKey((currentKey) => currentKey + 1);
  };

  const handleGridFormatItem = (grid, event) => {
    const binding = event.panel?.columns?.[event.col]?.binding;

    if (!REVIEW_COLUMN_BINDINGS.has(binding)) return;

    event.cell.classList.add(REVIEW_COLUMN_CLASS);

    if (event.panel === grid.columnHeaders) {
      event.cell.classList.add(REVIEW_COLUMN_HEADER_CLASS);
    }
  };

  const handleExportExcel = () => {
    if (!gridRef.current) return;

    FlexGridXlsxConverter.saveAsync(
      gridRef.current,
      {
        includeColumnHeaders: true,
        includeStyles: false,
      },
      "ExpensePayments.xlsx",
    );
  };

  const getPaymentId = (payment) =>
    payment?.paymentTransHeaderId ?? payment?.transHeaderId ?? payment?.id;

  const handleReviewStatusChange = (payment, value) => {
    onReviewPaymentChange?.(getPaymentId(payment), {
      expStatusId: value === "" ? null : Number(value),
    });
  };

  const handleApplyReviewComments = () => {
    if (!commentSelection || !canReview) return;

    onReviewPaymentChange?.(commentSelection.paymentId, {
      statusComments: commentSelection.draft,
    });
    setCommentSelection(null);
  };

  return (
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: "0.25rem",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
        overflow: "hidden",
        p: 1,
        position: "relative",
        ...reviewGridSx,
      }}
    >
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <SimpleColumnPicker
            ref={gridRef}
            refreshKey={columnPickerRefreshKey}
            storageKey="ExpensePaymentsGrid"
            forceHiddenCols={forceHiddenCols}
          />
          <Box boxShadow={2} sx={{ borderRadius: 50, height: 40, width: 40 }}>
            <Tooltip title={PAYMENT_GRID_LABELS.exportExcel}>
              <IconButton onClick={handleExportExcel}>
                <img src={excel} alt="excel" style={{ height: 24 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {canAddPayment && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<Add />}
              disabled={isAddPaymentDisabled}
              onClick={() => onAddPayment?.()}
              title={`${addPaymentText} (Alt+P)`}
              sx={{
                fontWeight: 800,
                minHeight: 32,
                whiteSpace: "nowrap",
              }}
            >
              {addPaymentText}
            </Button>
          )}
        </Box>
        <Typography
          sx={{
            color: "#101828",
            fontSize: 14,
            fontWeight: 500,
            ml: "auto",
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {PAYMENT_GRID_LABELS.totalPayments}: {formatCurrency(paidAmount)}
        </Typography>
      </Box>

      <FlexGrid
        autoGenerateColumns={false}
        className="explorer-grid"
        formatItem={handleGridFormatItem}
        headersVisibility="Column"
        initialized={handleGridInitialized}
        isReadOnly
        itemsSource={paymentsForGrid}
        selectionMode="Row"
        style={{}}
      >
        <FlexGridFilter />
        {hasRowActions && (
          <FlexGridColumn
            header=""
            width={78}
            minWidth={78}
            align="center"
            allowSorting={false}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => (
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 0.25,
                    height: "100%",
                    justifyContent: "center",
                  }}
                >
                  {ctx.item?.canEdit === true && (
                    <Tooltip title={PAYMENT_GRID_LABELS.editPayment}>
                      <IconButton
                        size="small"
                        color="primary"
                        sx={rowActionButtonSx}
                        onClick={(event) => {
                          event.stopPropagation();
                          onPaymentAction?.("editPayment", ctx.item);
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {ctx.item?.canDelete === true && (
                    <Tooltip title={PAYMENT_GRID_LABELS.deletePayment}>
                      <IconButton
                        size="small"
                        color="error"
                        sx={rowActionButtonSx}
                        onClick={(event) => {
                          event.stopPropagation();
                          onPaymentAction?.("deletePayment", ctx.item);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              )}
            />
          </FlexGridColumn>
        )}
        {/* <FlexGridColumn
          binding="transTypeName"
          header={PAYMENT_GRID_LABELS.paymentType}
          width={220}
          minWidth={180}
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => (
              <ReadOnlyCell
                value={ctx.item?.transTypeName ?? ctx.item?.transTypeAbbr}
              />
            )}
          />
        </FlexGridColumn> */}
        <FlexGridColumn
          binding="paymentDateFormatted"
          header={PAYMENT_GRID_LABELS.paymentDate}
          width={150}
          align="right"
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => (
              <ReadOnlyCell
                value={ctx.item?.paymentDateFormatted}
                align="right"
              />
            )}
          />
        </FlexGridColumn>
        <FlexGridColumn
          binding="paymentNumber"
          header={PAYMENT_GRID_LABELS.paymentNumber}
          width={150}
          minWidth={120}
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => <ReadOnlyCell value={ctx.item?.paymentNumber} />}
          />
        </FlexGridColumn>
        <FlexGridColumn
          binding="cashAmount"
          header={PAYMENT_GRID_LABELS.cashAmount}
          format="c€"
          width={140}
          align="right"
        />
        <FlexGridColumn
          binding="cardAmount"
          header={PAYMENT_GRID_LABELS.cardAmount}
          format="c€"
          width={140}
          align="right"
        />
        <FlexGridColumn
          binding="bankAmount"
          header={PAYMENT_GRID_LABELS.bankAmount}
          format="c€"
          width={140}
          align="right"
        />
        <FlexGridColumn
          binding="amount"
          header={PAYMENT_GRID_LABELS.amount}
          format="c€"
          width={140}
          align="right"
        />
        {showReviewStatus && (
          <FlexGridColumn
            binding="expStatusAbbr"
            header={PAYMENT_GRID_LABELS.reviewStatus}
            width={190}
            minWidth={170}
            allowSorting={!canReview}
            cssClass={REVIEW_COLUMN_CLASS}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => {
                const statusValue = normalizeStatusValue(
                  getPaymentReviewStatusId(ctx.item),
                );

                if (!canReview) {
                  return (
                    <ReadOnlyCell
                      value={getPaymentReviewStatusLabel(
                        ctx.item,
                        statusValue,
                      )}
                    />
                  );
                }

                return (
                  <TextField
                    select
                    fullWidth
                    variant="standard"
                    size="small"
                    value={statusValue}
                    onChange={(event) =>
                      handleReviewStatusChange(ctx.item, event.target.value)
                    }
                    onClick={stopGridEvent}
                    onKeyDown={stopGridEvent}
                    InputProps={{ disableUnderline: true }}
                    sx={reviewInputSx}
                  >
                    {PAYMENT_REVIEW_STATUS_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value || "pending"}
                        value={option.value}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                );
              }}
            />
          </FlexGridColumn>
        )}
        {showReviewStatus && (
          <FlexGridColumn
            binding="statusComments"
            header={PAYMENT_GRID_LABELS.reviewComments}
            width={260}
            minWidth={220}
            allowSorting={!canReview}
            cssClass={REVIEW_COLUMN_CLASS}
          >
            <FlexGridCellTemplate
              cellType="Cell"
              template={(ctx) => {
                const rowLabel = `Πληρωμή ${ctx.item?.paymentNumber ?? ctx.row.index + 1}`;

                return (
                  <ReviewCommentsCell
                    value={ctx.item?.statusComments}
                    canEdit={canReview}
                    rowLabel={rowLabel}
                    onOpen={() =>
                      setCommentSelection({
                        paymentId: getPaymentId(ctx.item),
                        rowLabel,
                        draft: ctx.item?.statusComments ?? "",
                      })
                    }
                  />
                );
              }}
            />
          </FlexGridColumn>
        )}
        <FlexGridColumn
          binding="comments"
          header={PAYMENT_GRID_LABELS.comments}
          width={220}
          minWidth={180}
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => <ReadOnlyCell value={ctx.item?.comments} />}
          />
        </FlexGridColumn>
        <FlexGridColumn
          binding="remarks"
          header={PAYMENT_GRID_LABELS.remarks}
          width="*"
          minWidth={220}
        >
          <FlexGridCellTemplate
            cellType="Cell"
            template={(ctx) => <ReadOnlyCell value={ctx.item?.remarks} />}
          />
        </FlexGridColumn>
      </FlexGrid>

      <ReviewCommentsDialog
        selection={commentSelection}
        canEdit={canReview}
        onChange={(draft) =>
          setCommentSelection((current) => ({ ...current, draft }))
        }
        onClose={() => setCommentSelection(null)}
        onApply={handleApplyReviewComments}
      />

      {payments.length === 0 && (
        <Typography
          sx={{
            color: "#6b7280",
            fontSize: 14,
            fontWeight: 400,
            textAlign: "center",
          }}
        >
          {PAYMENT_GRID_LABELS.noPayments}
        </Typography>
      )}
    </Box>
  );
};

export default ExpensePaymentsGrid;
